import SelectedScheme from "components/Scheme/SelectedScheme";
import { assetSortEnum, assetTypeEnum } from "kubb";
import type React from "react";
import CompanyNavLink from "../../../../components/navigation/CompanyNavLink";
import { OneCardPage } from "../../../../components/utils/OneCardPage";
import useGetParsedSearchString from "../../../../hooks/getParsedSearchString";
import { schemeObjectPageTitle } from "../../../../hooks/setPageTitle";
import AssetList from "../list/AssetList";
import GatewayConnectivityReport from "./GatewayConnectivityReport";
import { UnassignedGatewayConnectivityReportDataRow } from "./GatewayConnectivityReportDataRow";
import GatewayConnectivityReportHeaderRow from "./GatewayConnectivityReportHeaderRow";
import GatewayFilterForm, {
  type GatewayFilterParams,
} from "./GatewayFilterForm";

function parseGatewayFilterParams(
  params: Record<string, unknown>,
): GatewayFilterParams {
  // Parse array params from query string
  const toArray = (val: unknown): string[] | undefined => {
    if (!val) return undefined;
    if (Array.isArray(val)) return val as string[];
    if (typeof val === "string") return [val];
    return undefined;
  };

  const toStringValue = (val: unknown): string | undefined => {
    if (!val) return undefined;
    if (typeof val === "string") return val;
    return undefined;
  };

  return {
    manufacturer: toArray(params.manufacturer),
    model: toArray(params.model),
    firmware_version: toArray(params.firmware_version),
    configuration_state: toArray(params.configuration_state),
    connectivity: toArray(params.connectivity),
    auto_sync: toStringValue(params.auto_sync),
    sync_devices: toStringValue(params.sync_devices),
  };
}

export default function GatewayConnectivityReportPage(props: {
  assigned?: boolean;
}): React.ReactElement {
  const { assigned = true } = props;
  const { params: filterParams, ready } =
    useGetParsedSearchString<GatewayFilterParams>(parseGatewayFilterParams);

  if (!ready) {
    return <></>;
  }

  return (
    <OneCardPage
      headerTitle={assigned ? "Gateway Status" : "Unassigned Gateway Status"}
      breadcrumbs={[{ url: "/admin/report/gateway", name: "Gateway Status" }]}
      showSchemeInBreadcrumbs={assigned}
      setPageTitle={schemeObjectPageTitle(
        `Gateway Status${!assigned ? ": Unassigned" : ""}`,
      )}
    >
      <>
        {assigned ? (
          <CompanyNavLink to={{ pathname: "/admin/report/gateway/unassigned" }}>
            Switch to unassigned gateways
          </CompanyNavLink>
        ) : (
          <CompanyNavLink to={{ pathname: "/admin/report/gateway" }}>
            Switch to assigned gateways
          </CompanyNavLink>
        )}{" "}
        {assigned ? (
          <SelectedScheme>
            {(props) => (
              <GatewayConnectivityReport
                {...props}
                filterParams={filterParams}
              />
            )}
          </SelectedScheme>
        ) : (
          <SelectedScheme>
            {({ company_id }) => (
              <>
                <div className="mb-4">
                  <GatewayFilterForm
                    company_id={company_id}
                    params={filterParams || {}}
                    assetPositionId="NONE"
                  />
                </div>
                <AssetList
                  company_id={company_id}
                  assetRow={UnassignedGatewayConnectivityReportDataRow}
                  headerRow={GatewayConnectivityReportHeaderRow(false)(false)}
                  params={{
                    asset_type: [assetTypeEnum.gateway],
                    asset_position_id: "NONE",
                    sort: assetSortEnum.last_contact,
                    manufacturer: filterParams?.manufacturer,
                    model: filterParams?.model,
                    firmware_version: filterParams?.firmware_version,
                    configuration_state: filterParams?.configuration_state,
                    connectivity: filterParams?.connectivity,
                    auto_sync: filterParams?.auto_sync
                      ? filterParams.auto_sync === "true"
                      : undefined,
                    sync_devices: filterParams?.sync_devices
                      ? filterParams.sync_devices === "true"
                      : undefined,
                  }}
                />
              </>
            )}
          </SelectedScheme>
        )}
      </>
    </OneCardPage>
  );
}
